import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Typography,
  Tabs,
  Tab,
} from "@mui/material";
import { useState } from "react";
import { useLoaderData } from "react-router-dom";
import usePost from "../../app/usePost";
import { axiosApi } from "../../interceptors/axios";
import { Result } from "../../types/Result";
import AppleVerifyDetails from "./AppleVerifyDetails";
import { AppleVerifyResponse, ProductPurchase, Purchase } from "./type";
import AndrodiVerifyDetails from "./AndrodiVerifyDetails";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`purchase-tabpanel-${index}`}
      aria-labelledby={`purchase-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `purchase-tab-${index}`,
    "aria-controls": `purchase-tabpanel-${index}`,
  };
}

export default function PurchaseForm() {
  const { post, result, waiting } = usePost<AppleVerifyResponse>();
  const {
    post: postProductPurchase,
    result: resultProductPurchase,
    waiting: waitingProductPurchase,
  } = usePost<ProductPurchase>();
  const data = useLoaderData() as Result<Purchase>;
  const purchase = data?.data || null;
  const [tabValue, setTabValue] = useState(0);

  // Format date for better readability
  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Get badge color based on platform
  const getPlatformColor = (platform: string) => {
    switch (platform?.toLowerCase()) {
      case "android":
        return "success";
      case "ios":
        return "primary";
      default:
        return "default";
    }
  };

  // Get decoded token for iOS purchases
  const getIosDecodedToken = () => {
    // Only make the request if we don't already have the token data
    if (!result?.data) {
      post(
        axiosApi.post("api/purchases/verify-ios", {
          receiptData: purchase?.purchaseToken,
        })
      );
    }
  };

  // Get decoded token for iOS purchases
  const getAndoridDecodedToken = () => {
    // Only make the request if we don't already have the token data
    if (!resultProductPurchase?.data) {
      postProductPurchase(
        axiosApi.post("api/purchases/verify-android", {
          receiptData: purchase?.purchaseToken,
          productId: purchase?.productId,
        })
      );
    }
  };

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // If changing to token details tab, load the data if needed
    if (newValue === 1 && !result?.data) {
      if (purchase?.platform?.toLowerCase() === "ios") getIosDecodedToken();
      if (purchase?.platform?.toLowerCase() === "android")
        getAndoridDecodedToken();
    }
  };

  // Get badge color based on consumed status
  const getConsumedStatusColor = (isConsumed: boolean) => {
    return isConsumed ? "success" : "warning";
  };

  return (
    <Box sx={{ width: "100%", p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Sipariş Detayları
      </Typography>

      {purchase ? (
        <Box sx={{ width: "100%" }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="purchase information tabs"
            >
              <Tab label="Sipariş Bilgileri" {...a11yProps(0)} />
              <Tab label="Token Detayları" {...a11yProps(1)} />
            </Tabs>
          </Box>

          {/* Tab 1: Purchase Information */}
          <TabPanel value={tabValue} index={0}>
            <Card elevation={3} sx={{ mb: 4 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="h6" gutterBottom>
                      Temel Bilgiler
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 4 }}>
                          <Typography variant="subtitle2">
                            Sipariş ID:
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 8 }}>
                          <Typography>{purchase.orderId || "-"}</Typography>
                        </Grid>

                        <Grid size={{ xs: 4 }}>
                          <Typography variant="subtitle2">Ürün ID:</Typography>
                        </Grid>
                        <Grid size={{ xs: 8 }}>
                          <Typography>{purchase.productId || "-"}</Typography>
                        </Grid>

                        <Grid size={{ xs: 4 }}>
                          <Typography variant="subtitle2">Platform:</Typography>
                        </Grid>
                        <Grid size={{ xs: 8 }}>
                          <Chip
                            label={purchase.platform || "Bilinmiyor"}
                            color={getPlatformColor(purchase.platform)}
                            size="small"
                          />
                        </Grid>

                        <Grid size={{ xs: 4 }}>
                          <Typography variant="subtitle2">
                            Tüketim Durumu:
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 8 }}>
                          <Chip
                            label={
                              purchase.isConsumed ? "Tüketildi" : "Tüketilmedi"
                            }
                            color={getConsumedStatusColor(purchase.isConsumed)}
                            size="small"
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="h6" gutterBottom>
                      Zaman Bilgileri
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 5 }}>
                          <Typography variant="subtitle2">
                            Oluşturulma Zamanı:
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 7 }}>
                          <Typography>
                            {formatDate(purchase.creationTime)}
                          </Typography>
                        </Grid>

                        <Grid size={{ xs: 5 }}>
                          <Typography variant="subtitle2">
                            Son Düzenleme Zamanı:
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 7 }}>
                          <Typography>
                            {formatDate(purchase.lastModificationTime)}
                          </Typography>
                        </Grid>

                        <Grid size={{ xs: 5 }}>
                          <Typography variant="subtitle2">
                            Oluşturan ID:
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 7 }}>
                          <Typography>{purchase.creatorId || "-"}</Typography>
                        </Grid>

                        <Grid size={{ xs: 5 }}>
                          <Typography variant="subtitle2">
                            Son Düzenleyen ID:
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 7 }}>
                          <Typography>
                            {purchase.lastModifierId || "-"}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card elevation={3} sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Satın Alma Tokeni
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                  <Typography
                    sx={{ wordBreak: "break-all", fontFamily: "monospace" }}
                  >
                    {purchase.purchaseToken || "-"}
                  </Typography>
                </Paper>
              </CardContent>
            </Card>
          </TabPanel>

          {/* Tab 2: Token Details */}
          <TabPanel value={tabValue} index={1}>
            {waiting || waitingProductPurchase ? (
              <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>
                  Token bilgileri yükleniyor...
                </Typography>
              </Box>
            ) : purchase.platform === "ios" && result?.data ? (
              <AppleVerifyDetails data={result.data} isLoading={false} />
            ) : purchase.platform === "android" &&
              resultProductPurchase?.data ? (
              <AndrodiVerifyDetails purchase={resultProductPurchase.data!} />
            ) : (
              <Card elevation={3}>
                <CardContent>
                  <Typography color="error">
                    Token bilgileri yüklenemedi.
                  </Typography>
                </CardContent>
              </Card>
            )}
          </TabPanel>
        </Box>
      ) : (
        <Card elevation={3}>
          <CardContent>
            <Typography color="error">
              Sipariş bilgileri yüklenemedi veya bulunamadı.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
